/**
 * Reine Platzierungs-Geometrie fuer die Suchspiel-Section.
 *
 * Diese Datei greift bewusst nicht auf window oder document zu, damit sie mit
 * `node --test` ohne Browser geprueft werden kann.
 *
 * Rechteck-Konvention: { left, top, right, bottom } in DOKUMENT-Koordinaten.
 *
 * `var` und die Wiederverwendung eines schon vorhandenen window-Objekts, nicht
 * `const`: liegen zwei Suchspiel-Sections auf einer Seite, laedt der Browser
 * diese Datei zweimal, und im Theme-Editor passiert das bei jedem
 * Section-Rerender. Gemessen (Review-Runde 1): ein doppelt deklariertes
 * top-level `const` liefert dabei genau ein
 * `Uncaught SyntaxError: Identifier 'HuntGameGeometry' has already been declared`.
 * FUNKTIONAL harmlos -- der erste Durchlauf ist fertig, bevor das zweite Script
 * geparst wird --, aber jedes Error-Monitoring meldet es. Eine
 * `var`-Deklaration darf wiederholt werden und schweigt.
 *
 * Die top-level-Bindung BLEIBT (Task 2): assets/hunt-game.js greift sie ueber
 * denselben globalen Namen ab, und `module.exports` unten laesst `node --test`
 * die Datei ohne Browser laden. Der window-Zugriff ist darum durchgehend
 * `typeof`-geschuetzt.
 */
var HuntGameGeometry = (typeof window !== 'undefined' && window.HuntGameGeometry) || (function () {
  function rectWidth(rect) {
    return rect.right - rect.left;
  }

  function rectHeight(rect) {
    return rect.bottom - rect.top;
  }

  function rectCenter(rect) {
    return { x: (rect.left + rect.right) / 2, y: (rect.top + rect.bottom) / 2 };
  }

  function rectFromCenter(centerX, centerY, size) {
    const half = size / 2;
    return {
      left: centerX - half,
      top: centerY - half,
      right: centerX + half,
      bottom: centerY + half,
    };
  }

  function rectsOverlap(a, b) {
    return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
  }

  function rectContainsRect(outer, inner) {
    return (
      inner.left >= outer.left &&
      inner.top >= outer.top &&
      inner.right <= outer.right &&
      inner.bottom <= outer.bottom
    );
  }

  function distanceBetweenPoints(a, b) {
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  /**
   * Waehlt eine Objektgroesse aus der Spanne [base*(1-variation), base].
   * minSize ist eine harte Untergrenze fuer die Bedienbarkeit auf Touch-Geraeten
   * und gilt auch dann, wenn baseSize selbst darunter liegt.
   */
  function pickSize(baseSize, variationPercent, minSize, random) {
    const reduction = baseSize * (variationPercent / 100) * random();
    return Math.max(minSize, Math.round(baseSize - reduction));
  }

  /**
   * Teilt den Suchbereich in `count` horizontale Baender.
   * Ein Objekt pro Band garantiert Verteilung ueber die gesamte Scroll-Tiefe
   * und liefert den vertikalen Mindestabstand per Konstruktion.
   */
  function makeBands(areaTop, areaBottom, count) {
    if (count <= 0) return [];
    const bands = [];
    const height = (areaBottom - areaTop) / count;
    for (let i = 0; i < count; i += 1) {
      bands.push({
        top: areaTop + height * i,
        bottom: i === count - 1 ? areaBottom : areaTop + height * (i + 1),
      });
    }
    return bands;
  }

  /**
   * Legt ein Objekt so an eine Bildkante, dass peekPercent seiner Groesse ueber
   * diese Kante hinausragt. Der ueberlappende Rest wird per clipInset abgeschnitten.
   *
   * Der Beschnitt wird aus den tatsaechlichen Rechtecken berechnet, nicht direkt aus
   * peekPercent, damit die Werte auch nach einer Anpassung von `rect` stimmen.
   */
  function peekPlacement({ imageRect, size, peekPercent, edge, alongRatio }) {
    const horizontal = edge === 'left' || edge === 'right';
    // Freiraum entlang der Kante; bei zu kleinem Bild wird zentriert.
    const alongSpan = horizontal ? rectHeight(imageRect) - size : rectWidth(imageRect) - size;
    const alongStart = horizontal ? imageRect.top : imageRect.left;
    const alongOffset = alongSpan > 0 ? alongSpan * alongRatio : alongSpan / 2;
    return peekPlacementAtEdge({
      imageRect,
      size,
      peekPercent,
      edge,
      alongPos: alongStart + alongOffset,
    });
  }

  /**
   * Dieselbe Platzierung, aber mit VORGEGEBENER Position entlang der Kante
   * statt einer gewuerfelten Ratio. Das ist der Eingang fuer die
   * Neuberechnung nach einem Reflow.
   *
   * Warum es diesen zweiten Eingang gibt -- und warum die Rechnung fuer
   * visibleRect und clipInset nur EINMAL existieren darf:
   *
   * Die Neuberechnung hat bis zum Geraetetest nur das Objektrechteck
   * angefasst, ueber ratioX/ratioY -- also den Abstand zum Ankerrand
   * SKALIERT mit der neuen Ankergroesse -- und den Beschnitt stehen lassen.
   * Ein Peek-Objekt haengt aber an der KANTE, nicht an einem Anteil der
   * Ankerhoehe. Wurde das Bild nach dem Messen groesser oder kleiner (spaet
   * geladenes Bild, Umbruch, andere Schrifthoehe), driftete das Objekt von der
   * Kante weg, waehrend der Beschnitt weiter genauso viel verdeckte. Uebrig
   * blieb eine Objekt-Scheibe, die im Bild steckte oder darunter schwebte --
   * die beiden Screenshots aus dem Geraetetest.
   *
   * Gemessen, Bild 315x300, Objekt 110px, Peek 40 Prozent, Unterkante:
   *   Bildhoehe 300 -> 340: sichtbare Oberkante  8,8 px IM Bild
   *   Bildhoehe 300 -> 400: sichtbare Oberkante 22,0 px IM Bild
   *   Bildhoehe 300 -> 260: sichtbare Oberkante  8,8 px UNTER dem Bild
   *
   * Deshalb liefert diese Funktion Rechteck, sichtbares Rechteck UND Beschnitt
   * zusammen aus einem Bildrechteck. Ein zweiter, eigener Rechenweg fuer die
   * Neuberechnung waere genau die Doppelung, aus der der Fehler entstand.
   *
   * alongPos wird in die Ausdehnung des Bildes GEKLEMMT. Nach einem Reflow
   * kann der aus der Ratio gerechnete Wert ausserhalb des geschrumpften Bildes
   * liegen; das Objekt sass dann an der Kante, aber ohne Bild dahinter -- der
   * Beschnitt verdeckte eine Flaeche, hinter der nichts ist.
   */
  function peekPlacementAtEdge({ imageRect, size, peekPercent, edge, alongPos: requestedAlong }) {
    const visibleExtent = size * (peekPercent / 100);
    const horizontal = edge === 'left' || edge === 'right';

    const alongSpan = horizontal ? rectHeight(imageRect) - size : rectWidth(imageRect) - size;
    const alongStart = horizontal ? imageRect.top : imageRect.left;
    const alongPos =
      alongSpan > 0
        ? Math.min(Math.max(requestedAlong, alongStart), alongStart + alongSpan)
        : alongStart + alongSpan / 2;

    let rect;
    if (edge === 'bottom') {
      const bottom = imageRect.bottom + visibleExtent;
      rect = { left: alongPos, right: alongPos + size, top: bottom - size, bottom };
    } else if (edge === 'top') {
      const top = imageRect.top - visibleExtent;
      rect = { left: alongPos, right: alongPos + size, top, bottom: top + size };
    } else if (edge === 'right') {
      const right = imageRect.right + visibleExtent;
      rect = { top: alongPos, bottom: alongPos + size, left: right - size, right };
    } else {
      const left = imageRect.left - visibleExtent;
      rect = { top: alongPos, bottom: alongPos + size, left, right: left + size };
    }

    const visibleRect = {
      left: edge === 'right' ? Math.max(rect.left, imageRect.right) : rect.left,
      right: edge === 'left' ? Math.min(rect.right, imageRect.left) : rect.right,
      top: edge === 'bottom' ? Math.max(rect.top, imageRect.bottom) : rect.top,
      bottom: edge === 'top' ? Math.min(rect.bottom, imageRect.top) : rect.bottom,
    };

    const toPercent = (value, total) => (total === 0 ? 0 : Math.max(0, (value / total) * 100));
    const clipInset = {
      top: toPercent(visibleRect.top - rect.top, size),
      right: toPercent(rect.right - visibleRect.right, size),
      bottom: toPercent(rect.bottom - visibleRect.bottom, size),
      left: toPercent(visibleRect.left - rect.left, size),
    };

    return { rect, visibleRect, clipInset };
  }

  function shrinkRect(rect, margin) {
    return {
      left: rect.left + margin,
      top: rect.top + margin,
      right: rect.right - margin,
      bottom: rect.bottom - margin,
    };
  }

  function growRect(rect, margin) {
    return shrinkRect(rect, -margin);
  }

  /**
   * Die BOX eines Objekts: das Rechteck des Buttons, also gleichzeitig
   * Sichtfenster und Klickflaeche (overflow:hidden, Design-Spec 6.6).
   *
   * Ohne Puls ist die Box exakt das sichtbare Rechteck -- unveraenderte
   * Vorgabe: klickbar ist nur, was man sieht.
   *
   * Mit Puls braucht die Box Spielraum. Die Puls-Keyframe skaliert den SPAN um
   * `boxScale` (CSS: hunt-game-object-pulse, Faktor PULSE_HEADROOM in
   * assets/hunt-game.js), und der Span ist `size` x `size` gross. Er waechst
   * dabei um `size * (boxScale - 1) / 2` PRO SEITE. Genau dieser Betrag ist der
   * Spielraum -- nicht ein prozentualer Aufschlag auf das sichtbare Rechteck:
   * bei einem Peek-Objekt ist der sichtbare Streifen kleiner als `size`, ein
   * prozentualer Aufschlag waere also kleiner als das Wachstum des Spans und
   * wuerde am Puls-Hoehepunkt weiter beschneiden -- genau der gemeldete Fehler
   * (abgeschnittene Bildkanten). Bei einem Objekt ohne Beschnitt (sichtbares
   * Rechteck == volles Objekt) sind beide Rechnungen identisch, dort IST die
   * Box das mit boxScale skalierte sichtbare Rechteck.
   *
   * Folgen, die dokumentiert bleiben muessen:
   * 1. Die Box ist die HUELLE des pulsierenden Objekts. Sie wird als Ganzes
   *    gegen die Sperrflaechen geprueft (siehe placeObjects), die Klickflaeche
   *    bleibt also die geprueffte Flaeche. Am Puls-Hoehepunkt deckt sich der
   *    skalierte Span mit ihr; im Puls-Tal ragt sie um bis zu
   *    `size * (boxScale - 1) / 2` ueber die sichtbaren Pixel hinaus.
   * 2. Der Fussabdruck jedes Objekts waechst um `boxScale`, die Platzierung
   *    wird also etwas enger. Gemessen im Bericht zu dieser Aenderung.
   * 3. Bei einem Peek-Objekt waechst das Fenster auch an der Beschnitt-Kante:
   *    ein schmaler Streifen des zuvor verdeckten Teils wird sichtbar. Der
   *    Peek-Anteil sinkt dadurch geringfuegig -- und der Puls wird an
   *    Peek-Objekten ueberhaupt erst sichtbar, weil das Fenster den skalierten
   *    Span vorher an allen vier Seiten beschnitt.
   */
  function objectBox(visibleRect, size, boxScale, clipInset) {
    const scale = boxScale === undefined || boxScale === null ? 1 : boxScale;
    // Auch bei 1 eine KOPIE, nicht das Rechteck des Aufrufers: der Rueckgabewert
    // wandert in isPositionValid, in Sperrflaechen-Listen und (ueber
    // writeGeometry) in Stilwerte. Eine geteilte Referenz waere ein stiller
    // Kanal zwischen Pruefung und Quelle -- eine Zeile dagegen ist billiger als
    // die Suche danach.
    const margin = scale === 1 ? 0 : (size * (scale - 1)) / 2;
    /*
     * Der Spielraum gilt NUR auf den Seiten, an denen das Objekt nicht
     * beschnitten ist.
     *
     * Zweite Runde des Geraetetests: Objekte sahen im Bild abgeschnitten aus,
     * aber nur beim Pulsieren. Der Spielraum lag auf allen vier Seiten -- auch
     * auf der zum Anker hin. Dort gibt es nichts freizulegen, das Objekt laeuft
     * hinter dem Bild weiter. Das groessere Fenster legte genau diesen
     * verborgenen Teil offen, und die harte Schnittkante lag damit nicht mehr
     * AUF der Bildkante, wo das Bild sie verdeckt, sondern sichtbar auf dem
     * Bild.
     *
     * Gemessen im Browser, Objekt 110px, Peek 40 %, rechte Bildkante bei x=300:
     *   Puls aus: Fenster links 300,0  -- genau die Bildkante
     *   Puls an:  Fenster links 296,7  -- 3,3px IM Bild
     *
     * Auf den freien Seiten bleibt der Spielraum unveraendert noetig: der span
     * skaliert um seinen Mittelpunkt, und was dabei nach AUSSEN waechst, muss
     * ins Fenster passen. Nach innen waechst es tiefer hinter das Bild, dort
     * ist Beschnitt genau richtig.
     *
     * Fuer die Klickflaeche ist das eine VERKLEINERUNG, und die ist immer
     * unbedenklich: eine kleinere Box bleibt gegen Sperrflaechen und
     * Sicherheitsrand gueltig. Sie ruecht Design-Spec 6.6 ausserdem naeher --
     * klickbar ist, was man sieht.
     */
    const room = (side) => (margin === 0 || (clipInset && clipInset[side] > 0) ? 0 : margin);
    return {
      left: visibleRect.left - room('left'),
      top: visibleRect.top - room('top'),
      right: visibleRect.right + room('right'),
      bottom: visibleRect.bottom + room('bottom'),
    };
  }

  function nearestNeighbourDistance(rect, placedCenters) {
    if (placedCenters.length === 0) return Infinity;
    const center = rectCenter(rect);
    let smallest = Infinity;
    for (const other of placedCenters) {
      const d = distanceBetweenPoints(center, other);
      if (d < smallest) smallest = d;
    }
    return smallest;
  }

  /**
   * Bei Peek-Platzierungen wird hier das SICHTBARE Teilrechteck uebergeben,
   * nicht die volle Objekt-Box: Fuer den Kunden zaehlt nur, was er sieht.
   */
  /*
   * safeMargin ist eine ZAHL (beide Achsen) oder ein Objekt { x, y }.
   *
   * Warum pro Achse: der Rand war eine einzige Zahl (80) fuer alle vier
   * Seiten. Am Desktop sind das rund 11 % der Breite, auf einem
   * 360px-Bildschirm aber 44 % -- von 360px blieben 200px nutzbar. Gemessen
   * auf einer kurzen Kampagnenseite (746px hoch, 360px breit, 40 Seeds je
   * Wert): bei der STANDARD-Anzahl 3 lagen im Mittel nur 2,77 Objekte, bei 10
   * nur 3,63. Mit 28px horizontal sind es 3,00 und 5,72. Auf einer langen
   * Seite (2357px) und am Desktop aendert der kleinere Rand nichts -- dort
   * liegen mit 80 wie mit 28 immer alle Objekte.
   *
   * Und zur Begruendung, die vorher in Spec 6.5 stand ("sonst wird das Objekt
   * am Rand angeschnitten"): die war falsch. rectContainsRect verhindert das
   * Anschneiden schon bei safeMargin 0, denn es verlangt das GANZE Rechteck
   * innerhalb des Bereichs. Der Rand ist ein ABSTAND zum Rand -- und ein
   * Abstand darf pro Achse verschieden sein.
   */
  function insetRect(rect, safeMargin) {
    const x = typeof safeMargin === 'number' ? safeMargin : safeMargin.x;
    const y = typeof safeMargin === 'number' ? safeMargin : safeMargin.y;
    return { left: rect.left + x, top: rect.top + y, right: rect.right - x, bottom: rect.bottom - y };
  }

  function isPositionValid({ rect, blockedRects, placedCenters, minDistance, areaRect, safeMargin }) {
    if (!rectContainsRect(insetRect(areaRect, safeMargin), rect)) return false;
    for (const blocked of blockedRects) {
      if (rectsOverlap(rect, blocked)) return false;
    }
    return nearestNeighbourDistance(rect, placedCenters) >= minDistance;
  }

  const EDGES = ['bottom', 'top', 'bottom', 'top', 'left', 'right'];

  function rectOverlapsBand(rect, band) {
    return rect.top < band.bottom && rect.bottom > band.top;
  }

  // Baender haben nur top/bottom (horizontale Streifen ueber die volle Breite),
  // deshalb wird hier NUR vertikal auf das Band geclippt; links/rechts bleiben
  // die des Kandidaten-Rechtecks unveraendert.
  function clipRectToBand(rect, band) {
    return {
      left: rect.left,
      right: rect.right,
      top: Math.max(rect.top, band.top),
      bottom: Math.min(rect.bottom, band.bottom),
    };
  }

  function pointInBand(value, band) {
    return value >= band.top && value <= band.bottom;
  }

  /**
   * Nicht jede Kante einer Peek-Platzierung kann in jedem Band landen: bottom/top
   * haengen an der TATSAECHLICHEN Bildkante (peekPlacement darf diese Kante nicht
   * verschieben, sonst stimmt die Peek-Geometrie nicht mehr), waehrend left/right
   * ueber alongRatio entlang der vertikalen Bildausdehnung sampeln und deshalb auf
   * den Schnitt mit dem Band eingeschraenkt werden koennen (siehe clipRectToBand).
   * Nur Kanten, deren Platzierung tatsaechlich im angefragten Band liegen kann,
   * werden hier angeboten -- sonst wandert ein Objekt fuer ein mittleres Band an
   * die obere/untere Kante eines grossen Bildes, und die Verteilungsgarantie der
   * Baender geht fuer Bild-Anker verloren (Review Fix-Runde 2, Finding 2).
   */
  function eligibleEdgesForBand(imageRect, band) {
    return EDGES.filter((edge) => {
      if (edge === 'bottom') return pointInBand(imageRect.bottom, band);
      if (edge === 'top') return pointInBand(imageRect.top, band);
      return rectOverlapsBand(imageRect, band);
    });
  }

  /**
   * Hinweis fuer Aufrufer (Neuberechnung bei Resize): bei Peek-Platzierungen an
   * der oberen/linken Kante liegt rect VOR dem Anker, also wird ratioX bzw.
   * ratioY negativ. Das ist beabsichtigt und fuer die Rekonstruktion
   * (anchor.left + ratioX * anchorWidth) noetig -- der Wertebereich ist NICHT
   * auf [0,1] beschraenkt, sondern kann je nach Kante und peekPercent auch < 0
   * oder > 1 sein.
   */
  function anchorRatios(rect, anchorRect) {
    const w = rectWidth(anchorRect);
    const h = rectHeight(anchorRect);
    return {
      ratioX: w === 0 ? 0 : (rect.left - anchorRect.left) / w,
      ratioY: h === 0 ? 0 : (rect.top - anchorRect.top) / h,
    };
  }

  /**
   * Wie viele der `count` Objekte sollen hinter einem Bild liegen?
   *
   * `Math.max(1, ...)` ist Absicht: bei 3 Objekten und 10 % ergibt die reine
   * Rechnung 0,3 und wuerde auf 0 runden. Ein Regler, der bei seinem kleinsten
   * Wert gar nichts mehr tut, sieht fuer die Redaktion kaputt aus -- mindestens
   * ein Objekt lugt darum immer hinter einem Bild hervor, solange der Peek
   * ueberhaupt aktiv ist.
   */
  function peekTargetFor(count, enabled, share) {
    if (!enabled || count <= 0) return 0;
    const percent = typeof share === 'number' && Number.isFinite(share) ? share : 100;
    return Math.min(count, Math.max(1, Math.round((count * percent) / 100)));
  }

  function shuffleInPlace(items, random) {
    for (let i = items.length - 1; i > 0; i -= 1) {
      const j = Math.floor(random() * (i + 1)) % (i + 1);
      const swap = items[i];
      items[i] = items[j];
      items[j] = swap;
    }
    return items;
  }

  /**
   * Bauplan der Runde: ein Array aus `count` Booleans mit genau `peekTarget`
   * true-Werten, gemischt mit dem INJIZIERTEN `random` (Fisher-Yates) und
   * damit fuer einen gegebenen Seed reproduzierbar.
   *
   * Gemischt, NICHT "die ersten N sind Peek": Slot-Index i ist der Index des
   * Bandes, in dem das Objekt zuerst sucht (siehe placeObjects). Ein
   * ungemischter Plan legte alle Peek-Objekte in die obersten Baender und
   * damit an den Seitenanfang -- die Bild-Objekte klumpten oben, die
   * Flaechen-Objekte unten.
   *
   * `peekableSlots` nennt je Slot, ob im EIGENEN Band ueberhaupt ein
   * brauchbares Bild liegt. Diese Slots bekommen die Peek-Rollen zuerst,
   * innerhalb der Gruppe weiterhin gemischt.
   *
   * Grund, gemessen (Review Fix-Runde 1): ein Peek-Slot erschoepft laut
   * Fallback-Kette BEIDE Anker-Arten im eigenen Band, bevor er ein anderes
   * Band ansieht -- und ein Flaechen-Anker im eigenen Band gelingt fast immer.
   * Ein Peek-Slot, dessen Band kein brauchbares Bild enthaelt, landet also
   * praktisch immer auf einer Flaeche. Die erreichbare Peek-Zahl ist damit
   * durch die ANZAHL DER BAENDER MIT BRAUCHBAREM BILD begrenzt. Blind
   * verteilt verschenkte der Plan genau diese Rollen: bei 10 Objekten auf
   * 375px hatten zwei von zehn Baendern kein brauchbares Bild und trugen ueber
   * 80 Runden kein einziges Peek-Objekt, der Mittelwert lag bei 7,55 statt 10.
   *
   * Das Verteilungsziel leidet dadurch nicht: verteilt wird unter den
   * Baendern, die ueberhaupt peeken KOENNEN -- das ist strikt besser, als
   * blind zu verteilen und Rollen zu verlieren.
   *
   * Kein Mischen bei peekTarget 0 oder peekTarget >= count: dort ist das
   * Ergebnis eindeutig, und ein Mischen wuerde nur `random` verbrauchen.
   */
  function makePeekPlan(count, peekTarget, random, peekableSlots) {
    const plan = new Array(Math.max(0, count)).fill(false);
    if (peekTarget <= 0) return plan;
    if (peekTarget >= count) return plan.fill(true);

    // Slots, in deren eigenem Band ueberhaupt ein brauchbares Bild liegt,
    // kommen ZUERST -- innerhalb jeder Gruppe gemischt. Fehlt die Angabe,
    // gelten alle Slots als geeignet (Verhalten vor dieser Unterscheidung).
    const preferred = [];
    const rest = [];
    for (let i = 0; i < count; i += 1) {
      const canPeek = peekableSlots ? peekableSlots[i] === true : true;
      (canPeek ? preferred : rest).push(i);
    }
    const order = shuffleInPlace(preferred, random).concat(shuffleInPlace(rest, random));
    for (let i = 0; i < peekTarget && i < order.length; i += 1) plan[order[i]] = true;
    return plan;
  }

  /**
   * Platziert `count` Objekte im Suchbereich.
   *
   * total im Ergebnis ist die TATSAECHLICH platzierte Anzahl. Sie kann kleiner als
   * count sein, wenn der Platz nicht reicht. Der Counter im UI muss diesen Wert
   * verwenden, sonst gibt es ein unerreichbares Spielziel.
   *
   * Fallback-Kette pro Band (Spec 6.5): Bild-Anker im eigenen Band -> Flaechen-Anker
   * im eigenen Band -> beide in noch unbesetzten Baendern -> beide in bereits
   * besetzten Baendern -> beste Ersatzposition -> Objekt entfaellt. "Unbesetzt"
   * bezieht sich auf die SUCHREIHENFOLGE, nicht auf einen harten Ausschluss: ein
   * bereits belegtes Band bleibt als naechstbeste Praeferenz nutzbar (minDistance
   * gilt weiterhin), denn ein zweites Objekt in einem belegten Band ist fuer den
   * Kunden immer noch besser als ein entfallenes Objekt. Die Ersatzposition wird
   * ueber ALLE Stufen und Baender hinweg gesammelt (nicht nur die des zuletzt
   * versuchten Bandes) und erst herangezogen, wenn die gesamte Kette ohne
   * gueltigen Treffer bleibt. Eine Ersatzposition wird nur akzeptiert, wenn ihr
   * Abstand zum naechsten Nachbarn mindestens minDistance/2 betraegt
   * (Qualitaetsschwelle); sonst entfaellt das Objekt lieber ganz, statt zwei
   * Objekte sichtbar uebereinanderzulegen.
   *
   * PEEK-ANTEIL (peekConfig.share). `enable_image_peek` heisst nicht mehr
   * "jedes Objekt liegt hinter einem Bild": peekTargetFor() bestimmt, WIE VIELE
   * der Objekte es sein sollen, makePeekPlan() verteilt sie gemischt auf die
   * Slots. Der Plan aendert innerhalb eines Slots nur die REIHENFOLGE der
   * Anker-Arten:
   *   Peek-Slot      -> Bild-Anker zuerst, Flaechen-Anker als Rueckfall.
   *   Nicht-Peek-Slot -> Flaechen-Anker zuerst, Bild-Anker als letzter
   *                      Rueckfall VOR Ersatzposition und Entfallen.
   * Alles andere -- Baender, Reihenfolge der Baender, Ersatzposition,
   * Qualitaetsschwelle, Entfallen -- bleibt unveraendert.
   *
   * Der Bild-Anker bleibt fuer einen Nicht-Peek-Slot bewusst erreichbar: ein
   * platziertes Objekt ist besser als ein entfallenes. Dokumentierte Folge --
   * der WIRKSAME Anteil kann darum nach OBEN abweichen, wenn freie Flaechen
   * knapp sind (viele Sperrflaechen, kurze Seite, wenige .shopify-section-
   * Wrapper). Nach unten kann er nur abweichen, wenn es zu wenige brauchbare
   * Bilder gibt. `share` ist eine Zielvorgabe, keine Garantie.
   */
  function placeObjects(config) {
    const {
      count, areaRect, imageCandidates, surfaceCandidates, blockedRects,
      sizeConfig, peekConfig, minDistance, safeMargin, maxAttempts, random,
    } = config;
    const minImageSize = config.minImageSize ?? 120;
    // Puls-Spielraum. 1 heisst: Box == sichtbares Rechteck (siehe objectBox).
    const boxScale = config.boxScale ?? 1;

    const bands = makeBands(areaRect.top, areaRect.bottom, count);
    const positions = [];
    const placedCenters = [];

    const usableImages = peekConfig.enabled
      ? imageCandidates.filter(
          (c) => rectWidth(c.rect) >= minImageSize && rectHeight(c.rect) >= minImageSize
        )
      : [];

    // Ein vom Aufrufer VORGEGEBENER Bauplan sticht die Zufallsverteilung. Das
    // braucht die Ersatzplatzierung nach einem Reflow: sie ruft placeObjects
    // mit count 1 auf, und peekTargetFor liefert dort wegen der
    // Mindestvorgabe (max(1, ...)) immer 1 -- ein Objekt, das auf einer freien
    // Flaeche lag, waere nach jedem Umbruch ein Bild-Objekt geworden und der
    // wirksame Anteil driftete mit jeder Fensteraenderung nach oben. Mit dem
    // vorgegebenen Plan behaelt jedes Objekt seinen Charakter, so wie es nach
    // Design-Spec 6.7 auch seinen Anker behaelt.
    const givenPlan =
      Array.isArray(peekConfig.plan) && peekConfig.plan.length === count
        ? peekConfig.plan.map((value) => peekConfig.enabled && value === true)
        : null;
    const peekTarget = peekTargetFor(count, peekConfig.enabled, peekConfig.share);
    // Welche Baender koennen ueberhaupt ein Peek-Objekt tragen? Dieselbe
    // Bedingung, die tryCandidates fuer Peek-Kandidaten anwendet -- sonst
    // verspricht der Bauplan Rollen, die die Kette nicht einloesen kann.
    const peekableBands = bands.map((band) =>
      usableImages.some((candidate) => eligibleEdgesForBand(candidate.rect, band).length > 0)
    );
    const peekPlan = givenPlan || makePeekPlan(count, peekTarget, random, peekableBands);

    // Liefert { result, best } getrennt: result ist nur gesetzt, wenn eine Position
    // ALLE Kriterien inklusive minDistance erfuellt. best ist die raeumlich gueltige
    // Position mit dem groessten Abstand zum naechsten Nachbarn, unabhaengig davon,
    // ob minDistance erreicht wurde -- der Aufrufer entscheidet ueber die gesamte
    // Kette hinweg, ob und wann best als Ersatzposition herangezogen wird.
    const tryCandidates = (candidates, band, usePeek) => {
      // Bei Peek-Kandidaten reicht ein blosses rectOverlapsBand nicht: ein
      // Kandidat ist nur brauchbar, wenn mindestens eine Kante fuer DIESES
      // Band ueberhaupt eligible ist (siehe eligibleEdgesForBand).
      const inBand = usePeek
        ? candidates.filter((c) => eligibleEdgesForBand(c.rect, band).length > 0)
        : candidates.filter((c) => rectOverlapsBand(c.rect, band));
      if (inBand.length === 0) return { result: null, best: null };

      // Mindestgroesse PRO PLATZIERUNG, nicht pro Runde.
      //
      // Bei einer Peek-Platzierung ist nur `peekPercent` des Objekts sichtbar;
      // damit dieser Streifen das 44px-Tap-Ziel erreicht, muss das ganze Objekt
      // auf `ceil(44 / (peekPercent/100))` wachsen (minSizePeek). Bei einer
      // Flaechen-Platzierung ist das ganze Objekt sichtbar -- dort gilt die
      // nackten 44px (minSize). Frueher hing die erhoehte Untergrenze allein an
      // `peekConfig.enabled` und traf damit auch Flaechen-Anker, die gar nichts
      // zu beschneiden haben: sie wurden ohne Gegenwert auf 110px aufgeblasen.
      // Mit einem Peek-ANTEIL unter 100 % waere das offensichtlich geworden.
      const minSizeHere = usePeek ? sizeConfig.minSizePeek ?? sizeConfig.minSize : sizeConfig.minSize;

      let best = null;
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const candidate = inBand[Math.floor(random() * inBand.length) % inBand.length];
        const size = pickSize(
          sizeConfig.baseSize, sizeConfig.variationPercent, minSizeHere, random
        );

        let placement;
        if (usePeek) {
          // Nur unter den fuer dieses Band eligiblen Kanten auswaehlen (Gewichtung
          // von EDGES bleibt unter den verbleibenden Kanten erhalten). left/right
          // sampeln ueber alongRatio innerhalb des auf das Band geclippten Bildes,
          // bottom/top nutzen bewusst das UNGEKUERZTE Bild -- ihre Position haengt
          // an der echten Bildkante, nicht an alongRatio.
          const pool = eligibleEdgesForBand(candidate.rect, band);
          const edge = pool[Math.floor(random() * pool.length) % pool.length];
          const imageRectForEdge =
            edge === 'left' || edge === 'right' ? clipRectToBand(candidate.rect, band) : candidate.rect;
          placement = peekPlacement({
            imageRect: imageRectForEdge, size, peekPercent: peekConfig.peekPercent,
            edge, alongRatio: random(),
          });
          placement.edge = edge;
        } else {
          // Das Band entscheidet nur, WELCHE Kandidaten in Frage kommen. Gesampelt
          // wird innerhalb des Schnitts von Kandidat und Band, sonst koennte ein
          // grossflaechiger Kandidat (z.B. eine grosse freie Flaeche) trotzdem
          // ausserhalb seines Bandes landen und die Verteilungsgarantie der Baender
          // (ein Objekt pro Band ueber die gesamte Scroll-Tiefe) aushebeln.
          const sampleRect = clipRectToBand(candidate.rect, band);
          const spanX = Math.max(0, rectWidth(sampleRect) - size);
          const spanY = Math.max(0, rectHeight(sampleRect) - size);
          const left = sampleRect.left + spanX * random();
          const top = sampleRect.top + spanY * random();
          const rect = { left, top, right: left + size, bottom: top + size };
          placement = { rect, visibleRect: rect, clipInset: null, edge: null };
        }

        // Geprueft wird die BOX, nicht das sichtbare Rechteck: die Box ist die
        // Klickflaeche des Buttons (Design-Spec 6.6 -- klickbar ist nur die
        // geprueffte Flaeche). Ohne Puls sind beide identisch; mit Puls ist die
        // Box das um den Puls-Spielraum erweiterte Rechteck (siehe objectBox),
        // und genau diese groessere Flaeche muss die Pruefung bestehen.
        const box = objectBox(placement.visibleRect, size, boxScale, placement.clipInset);
        const args = {
          rect: box, blockedRects, placedCenters,
          minDistance, areaRect, safeMargin,
        };

        if (isPositionValid(args)) {
          return { result: { candidate, size, placement }, best };
        }

        // Ersatzposition mitfuehren: raeumlich zulaessig (Sperrflaechen und
        // Sicherheitsrand bleiben voll in Kraft), nur der Mindestabstand wird
        // fuer diese Pruefung ignoriert.
        const spatiallyOk =
          isPositionValid(Object.assign({}, args, { placedCenters: [], minDistance: 0 }));
        if (spatiallyOk) {
          const score = nearestNeighbourDistance(box, placedCenters);
          if (best === null || score > best.score) {
            best = { candidate, size, placement, score };
          }
        }
      }
      return { result: null, best };
    };

    // Belegung je Band-Index: bestimmt NUR die Suchreihenfolge (unbesetzte
    // Baender zuerst, dann bereits besetzte), keine harte Ausschluss-Regel --
    // minDistance bleibt in jedem Band die eigentliche Zulaessigkeitspruefung.
    const bandOccupied = new Array(bands.length).fill(false);

    for (let i = 0; i < bands.length; i += 1) {
      const unoccupied = [];
      const occupied = [];
      for (let j = 0; j < bands.length; j += 1) {
        if (j === i) continue;
        (bandOccupied[j] ? occupied : unoccupied).push(j);
      }
      // Reihenfolge (Spec 6.5): eigenes Band -> noch unbesetzte Baender ->
      // bereits besetzte Baender als letzte Praeferenz vor der Ersatzposition.
      const orderIndices = [i, ...unoccupied, ...occupied];

      let result = null;
      let bestOverall = null;
      let winningIndex = i;
      let bestOverallIndex = i;

      // Der Bauplan dreht NUR diese Reihenfolge: ein Peek-Slot probiert
      // Bild-Anker zuerst, ein Nicht-Peek-Slot Flaechen-Anker zuerst. Beide
      // Arten bleiben in jedem Band erreichbar -- ein Objekt zu platzieren ist
      // besser, als es fallen zu lassen (siehe die Anmerkung zum wirksamen
      // Anteil im Kopfkommentar).
      const wantsPeek = peekPlan[i] === true;
      const sources = wantsPeek
        ? [{ candidates: usableImages, usePeek: true }, { candidates: surfaceCandidates, usePeek: false }]
        : [{ candidates: surfaceCandidates, usePeek: false }, { candidates: usableImages, usePeek: true }];

      /*
       * ANKERART aussen, BAND innen -- und das ist die Reihenfolge, auf die es
       * ankommt.
       *
       * Vorher lief das Band aussen: fuer einen Peek-Platz wurde pro Band erst
       * der Bild-Anker und dann sofort der FLAECHEN-Anker probiert, bevor
       * ueberhaupt ein anderes Band mit einem Bild-Anker an die Reihe kam. Ein
       * Band ohne brauchbares Bild fiel damit auf eine freie Flaeche zurueck,
       * obwohl im Nachbarband ein Bild bereitstand. Gemessen mit Bildern in 2
       * von 5 Baendern und Regler auf 100 %: 200 von 200 Runden ergaben genau
       * 2 von 5 Objekten hinter einem Bild -- der Anteil war durch die Bandlage
       * gedeckelt und der Regler oberhalb dieser Grenze wirkungslos.
       *
       * Jetzt wird der GEWUENSCHTE Anker in allen Baendern durchprobiert, bevor
       * die andere Art dran ist. Die Band-Vorliebe (eigenes Band, dann
       * unbesetzte, dann besetzte) bleibt innerhalb jedes Durchgangs voll
       * erhalten -- sie entscheidet weiter, WO innerhalb einer Ankerart
       * gesucht wird, nur nicht mehr darueber, WELCHE Ankerart gewinnt.
       *
       * Der Tausch verschiebt damit bewusst eine Abwaegung: die Verteilung
       * ueber die Scroll-Tiefe war vorher die staerkere Vorliebe, jetzt ist es
       * die Ankerart. Das ist der Sinn des Reglers -- er soll sagen, wie viele
       * Objekte hinter Bildern liegen, und nicht von der Bildlage der Seite
       * ueberstimmt werden.
       */
      for (const source of sources) {
        for (const j of orderIndices) {
          const band = bands[j];
          const attempt = tryCandidates(source.candidates, band, source.usePeek);
          if (attempt.result) {
            result = attempt.result;
            winningIndex = j;
            break;
          }
          if (attempt.best && (bestOverall === null || attempt.best.score > bestOverall.score)) {
            bestOverall = attempt.best;
            bestOverallIndex = j;
          }
        }
        if (result) break;
      }

      // Stufe 4: erst NACH der gesamten Kette (eigenes Band, unbesetzte Baender,
      // besetzte Baender, Bild- und Flaechen-Anker) auf die beste Ersatzposition
      // zurueckfallen -- und auch dann nur, wenn sie die Qualitaetsschwelle
      // erreicht. Sonst entfaellt das Objekt (Stufe 5): ein verworfenes Objekt
      // kostet nichts, weil total unten aus positions.length berechnet wird,
      // aber ein Objekt unter einem anderen wuerde das Spiel de facto
      // unloesbar machen.
      if (!result && bestOverall && bestOverall.score >= minDistance / 2) {
        result = bestOverall;
        winningIndex = bestOverallIndex;
      }

      if (!result) continue;

      bandOccupied[winningIndex] = true;

      const { candidate, size, placement } = result;
      const ratios = anchorRatios(placement.rect, candidate.rect);
      positions.push({
        size,
        rect: placement.rect,
        visibleRect: placement.visibleRect,
        clipInset: placement.clipInset,
        anchor: candidate.anchor,
        ratioX: ratios.ratioX,
        ratioY: ratios.ratioY,
        edge: placement.edge,
      });
      placedCenters.push(rectCenter(placement.visibleRect));
    }

    return { positions, total: positions.length };
  }

  return {
    rectWidth,
    rectHeight,
    rectCenter,
    rectFromCenter,
    rectsOverlap,
    rectContainsRect,
    distanceBetweenPoints,
    pickSize,
    makeBands,
    peekPlacement,
    peekPlacementAtEdge,
    anchorRatios,
    shrinkRect,
    insetRect,
    growRect,
    objectBox,
    peekTargetFor,
    makePeekPlan,
    eligibleEdgesForBand,
    nearestNeighbourDistance,
    isPositionValid,
    placeObjects,
  };
})();

if (typeof window !== 'undefined') {
  window.HuntGameGeometry = HuntGameGeometry;
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = HuntGameGeometry;
}
